import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CARD_SURFACE } from "../components/discover/DiscoverCampaignCard";
import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import { PopUpSelect } from "../components/ds/PopUpButton";
import Menu, { MenuItem } from "../components/ds/Menu";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";
import StatCell from "../components/ds/StatCell";
import { Spinner } from "../components/ds/icons";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { getAffiliateMarketplace, getMyAffiliateLinks } from "../api/affiliate";
import AffiliateProgramModal from "../components/AffiliateProgramModal";
import PageTransition from "../components/motion/PageTransition";
import { shareOrCopyUrl } from "../utils/share";
import { SpaceLink, openOnKey } from "../components/discover/DiscoverAll";

/** The promoted product's own page, from inside a row that opens the program. */
const ProductLink = ({ row, className = "", children }) =>
  row.productId && row.mintSpaceId ? (
    <Link
      to={`/mint-space/${row.mintSpaceId}/product/${row.productId}`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      className={`${className} transition hover:text-accent-text hover:underline`}
    >
      {children}
    </Link>
  ) : (
    <span className={className}>{children}</span>
  );

/** The space behind a program; it opens with the join card for someone outside it. */
const CompanyLink = ({ row, className, children }) =>
  row.mintSpaceId ? (
    <span onKeyDown={(event) => event.stopPropagation()}>
      <SpaceLink space={{ _id: row.mintSpaceId, name: row.company, logo: row.logo }} className={className}>
        {children}
      </SpaceLink>
    </span>
  ) : (
    <span className={className}>{children}</span>
  );

const PAGE_SIZES = [10, 25, 50];
const CURRENCY = "₾";

/** 12500 → "12.5k". Keeps the money columns readable at table density. */
const compact = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return n.toFixed(n % 1 === 0 ? 0 : 2);
};

const formatCount = (value) => (Number(value) || 0).toLocaleString();

/** Page buttons with an ellipsis once there are more pages than fit. */
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
  if (current >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
};

const AffiliateMarketplacePage = ({ isDark = true, setToast }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [searchParams] = useSearchParams();
  // On a program only the space's members are shown, so they know why they see it.
  const membersTag = (
    <Badge size="s" className="ml-space-4 align-middle">
      {t("affiliate.membersOnlyTag")}
    </Badge>
  );
  // ?tab=my-links opens on your own links — where a "you're an affiliate now" notice lands.
  const [tab, setTab] = useState(() =>
    searchParams.get("tab") === "my-links" ? "my-links" : "marketplace"
  );
  const [rows, setRows] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [industryOpen, setIndustryOpen] = useState(false);
  const industryRef = useRef(null);
  const closeIndustry = useCallback(() => setIndustryOpen(false), []);
  const [sort, setSort] = useState("affiliateEarnings");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selected, setSelected] = useState(null);
  const [myLinks, setMyLinks] = useState([]);
  const [myTotals, setMyTotals] = useState(null);
  const [myLoading, setMyLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const loadMarketplace = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAffiliateMarketplace({
        search: debouncedSearch,
        industry,
        sort,
        order,
        page,
        limit,
      });
      if (res?.success) {
        setRows(res.data || []);
        setIndustries(res.industries || []);
        setPagination(res.pagination || { page: 1, limit, total: 0, pages: 1 });
      }
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, industry, sort, order, page, limit, setToast, t]);

  useEffect(() => {
    if (tab === "marketplace") loadMarketplace();
  }, [tab, loadMarketplace]);

  const loadMyLinks = useCallback(async () => {
    if (!isAuthenticated) return;
    setMyLoading(true);
    try {
      const res = await getMyAffiliateLinks();
      if (res?.success) {
        setMyLinks(res.data || []);
        setMyTotals(res.totals || null);
      }
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorLoading"));
    } finally {
      setMyLoading(false);
    }
  }, [isAuthenticated, setToast, t]);

  useEffect(() => {
    if (tab === "my-links") loadMyLinks();
  }, [tab, loadMyLinks]);

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
    setPage(1);
  };

  const handleJoined = (programId, data) => {
    setRows((prev) =>
      prev.map((r) =>
        r.programId === programId
          ? { ...r, myStatus: data.status, myCode: data.code, myLink: data.link }
          : r
      )
    );
    setSelected((prev) =>
      prev && prev.programId === programId
        ? { ...prev, myStatus: data.status, myCode: data.code, myLink: data.link }
        : prev
    );
  };

  const copyLink = async (link, code) => {
    await shareOrCopyUrl(link, setToast, t);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  const showingFrom = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const showingTo = Math.min(pagination.page * pagination.limit, pagination.total);

  // Figma's widths fit the English titles; the Georgian ones run longer.
  const columns = useMemo(() => {
    const widths = language === "ka" ? [195, 147, 96, 110, 202, 115, 84, 163] : [220, 120, 80, 150, 160, 120, 76, 186];
    return [
      { id: "company", label: t("affiliate.product") },
      { id: "industryType", label: t("affiliate.industryType") },
      { id: "productPrice", label: t("affiliate.price"), sortable: true, numeric: true },
      { id: "commissionRate", label: t("affiliate.commissionRate"), sortable: true, numeric: true },
      { id: "affiliateEarnings", label: t("affiliate.affiliateEarnings"), sortable: true, numeric: true },
      { id: "conversions", label: t("affiliate.conversions"), sortable: true, numeric: true },
      { id: "clicks", label: t("affiliate.clicks"), sortable: true, numeric: true },
      { id: "epc", label: t("affiliate.earningsPerClick"), numeric: true },
    ].map((col, index) => ({ ...col, width: widths[index] }));
  }, [t, language]);

  const commissionText = (row) =>
    row.commissionType === "fixed"
      ? `${CURRENCY}${row.commissionAmount}`
      : row.commissionRate > 0
        ? `${row.commissionRate}%`
        : "–";

  const priceText = (row) =>
    `${CURRENCY}${row.productPrice}${row.productBillingType === "recurring" ? ` / ${t("affiliate.perMonth")}` : ""}`;

  const payoutText = (row) =>
    row.holdDays > 0 ? t("affiliate.payoutInDays", { days: row.holdDays }) : t("affiliate.payoutRightAway");

  const thumbnail = (row, size) => (
    <span
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-fill-quaternary text-label-tertiary ${size}`}
    >
      {row.logo ? <img src={row.logo} alt="" className="h-full w-full object-cover" /> : <Icon name="image" size={20} />}
    </span>
  );

  const searchField = (className) => (
    <SearchField
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder={t("common.search")}
      className={className}
    />
  );

  const industryFilter = (
    <div className="relative self-start">
      <div ref={industryRef} className="flex items-center gap-space-4">
        <Button size="s" variant="gray" icon={<Icon name="plus" size={16} />} onClick={() => setIndustryOpen((open) => !open)}>
          {industry || t("affiliate.industryType")}
        </Button>
        {industry && (
          <IconButton
            label={t("common.close")}
            variant="plain"
            onClick={() => {
              setIndustry("");
              setPage(1);
            }}
          >
            <Icon name="close" size={16} />
          </IconButton>
        )}
      </div>
      <Menu
        open={industryOpen}
        onClose={closeIndustry}
        anchorRef={industryRef}
        align="start"
        label={t("affiliate.industryType")}
      >
        {industries.length === 0 ? (
          <p className="px-space-12 py-space-8 text-footnote text-label-secondary">{t("affiliate.noIndustries")}</p>
        ) : (
          industries.map((ind) => (
            <MenuItem
              key={ind}
              selected={ind === industry}
              detail={ind === industry ? <Icon name="check" size={20} className="shrink-0 text-accent-text" /> : null}
              onClick={() => {
                setIndustry(ind === industry ? "" : ind);
                setPage(1);
              }}
            >
              {ind}
            </MenuItem>
          ))
        )}
      </Menu>
    </div>
  );

  const pageButtons = (
    <div className="flex items-center gap-space-4">
      <button
        type="button"
        disabled={pagination.page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className="flex size-11 items-center justify-center rounded-[12px] text-label-primary hover:bg-fill-quaternary disabled:text-label-tertiary"
        aria-label={t("common.previous")}
      >
        <Icon name="chevron-left" size={20} />
      </button>
      {pageWindow(pagination.page, pagination.pages).map((p, index) =>
        p === "…" ? (
          <span key={`gap-${index}`} className="px-space-4 text-subheadline text-label-secondary">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => setPage(p)}
            aria-current={p === pagination.page ? "page" : undefined}
            className={`size-11 rounded-[12px] text-subheadline-semibold ${
              p === pagination.page ? "bg-fill-secondary text-label-primary" : "text-label-secondary hover:bg-fill-quaternary"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        disabled={pagination.page >= pagination.pages}
        onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
        className="flex size-11 items-center justify-center rounded-[12px] text-label-primary hover:bg-fill-quaternary disabled:text-label-tertiary"
        aria-label={t("common.next")}
      >
        <Icon name="chevron-right" size={20} />
      </button>
    </div>
  );

  const renderMarketplace = () => (
    <>
      {/* Figma B3 "Table": tablet and up */}
      <div className="hidden overflow-hidden rounded-radius-lg bg-bg-raised min-[700px]:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1112px] table-fixed border-collapse text-left">
            <colgroup>
              {columns.map((col) => (
                <col key={col.id} style={{ width: col.width }} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-separator-default bg-bg-inset">
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={`h-10 whitespace-nowrap text-caps-label font-normal uppercase text-label-secondary ${
                      col.numeric ? "pl-space-8 pr-space-16 text-right" : language === "ka" ? "px-space-12" : "px-space-16"
                    }`}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        className={`flex w-full items-center gap-space-4 uppercase ${col.numeric ? "justify-end" : ""}`}
                        onClick={() => toggleSort(col.id)}
                      >
                        {col.label}
                        {sort === col.id && <Icon name={order === "desc" ? "chevron-down" : "chevron-up"} size={14} className="text-accent-text" />}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-subheadline text-label-primary">
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="py-space-40 text-center">
                    <Spinner size={24} className="mx-auto text-accent-text" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <EmptyState icon="users" title={t("affiliate.noPrograms")} />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.programId}
                    onClick={() => setSelected(row)}
                    className="h-[64px] cursor-pointer border-b border-separator-default transition-colors hover:bg-fill-quaternary"
                  >
                    <td className="px-space-16">
                      <div className="flex min-w-0 items-center gap-space-12">
                        {thumbnail(row, "size-10")}
                        {/* The product is what's being promoted; the space is the context. */}
                        <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
                          <p className="truncate text-subheadline-semibold">
                            <ProductLink row={row}>{row.product || row.company}</ProductLink>
                            {row.visibility === "members" && membersTag}
                          </p>
                          <p className="truncate">
                            <CompanyLink row={row} className="text-footnote text-label-secondary">
                              {row.company}
                            </CompanyLink>
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="truncate px-space-16">{row.industryType || "–"}</td>
                    <td className="pr-space-16">
                      <span className="flex justify-end whitespace-nowrap">{priceText(row)}</span>
                    </td>
                    <td className="px-space-16 text-right text-accent-text">{commissionText(row)}</td>
                    <td className="px-space-16 text-right">
                      {CURRENCY}
                      {compact(row.affiliateEarnings)}
                    </td>
                    <td className="px-space-16 text-right">{formatCount(row.conversions)}</td>
                    <td className="px-space-16 text-right">{formatCount(row.clicks)}</td>
                    <td className="px-space-16 text-right">
                      {CURRENCY}
                      {compact(row.earningsPerClick)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-space-8 px-space-16 py-space-8">
          <span className="text-footnote text-label-secondary">
            {t("affiliate.showingRange")
              .replace("{from}", showingFrom)
              .replace("{to}", showingTo)
              .replace("{total}", formatCount(pagination.total))}
          </span>
          {pageButtons}
          <label className="flex items-center gap-space-8 text-footnote text-label-secondary">
            {t("affiliate.show")}
            <PopUpSelect
              label={t("affiliate.show")}
              value={String(limit)}
              onChange={(value) => {
                setLimit(Number(value));
                setPage(1);
              }}
              className="min-w-[80px]"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PopUpSelect>
          </label>
        </div>
      </div>

      {/* Figma "Market row": phones */}
      <div className="flex flex-col gap-space-16 min-[700px]:hidden">
        {loading ? (
          <div className="flex justify-center py-space-40">
            <Spinner size={24} className="text-accent-text" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon="users" title={t("affiliate.noPrograms")} />
        ) : (
          <>
            {rows.map((row) => (
              <div
                key={row.programId}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(row)}
                onKeyDown={openOnKey(() => setSelected(row))}
                className={`${CARD_SURFACE} !h-auto !rounded-[12px] cursor-pointer gap-space-12 p-space-16 text-left`}
              >
                <span className="flex min-w-0 items-center gap-space-12">
                  {thumbnail(row, "size-12")}
                  <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                    <span className="truncate text-headline text-label-primary">
                      <ProductLink row={row}>{row.product || row.company}</ProductLink>
                      {row.visibility === "members" && membersTag}
                    </span>
                    <span className="truncate text-footnote text-label-secondary">
                      <CompanyLink row={row} className="text-label-secondary">
                        {row.company}
                      </CompanyLink>
                      {row.industryType && ` · ${row.industryType}`}
                    </span>
                  </span>
                </span>
                <span className="flex flex-wrap gap-space-16">
                  {[
                    [t("affiliate.price"), priceText(row)],
                    [t("affiliate.commissionRate"), commissionText(row)],
                    [t("affiliate.payoutLabel"), payoutText(row)],
                  ].map(([label, value]) => (
                    <span key={label} className="flex flex-col gap-[2px]">
                      <span className="text-footnote text-label-secondary">{label}</span>
                      <span className="text-subheadline-semibold text-label-primary">{value}</span>
                    </span>
                  ))}
                </span>
              </div>
            ))}
            {pagination.pages > 1 && <div className="flex justify-center">{pageButtons}</div>}
          </>
        )}
      </div>
    </>
  );

  const renderMyLinks = () => {
    if (!isAuthenticated) {
      return (
        <EmptyState icon="link" title={t("affiliate.signInForLinks")} actionLabel={t("auth.signIn")} onAction={() => navigate("/auth")} />
      );
    }

    if (myLoading) {
      return (
        <div className="flex justify-center py-space-40">
          <Spinner size={24} className="text-accent-text" />
        </div>
      );
    }

    if (myLinks.length === 0) {
      return (
        <EmptyState
          icon="link"
          title={t("affiliate.noLinksYet")}
          actionLabel={t("affiliate.browsePrograms")}
          onAction={() => setTab("marketplace")}
        />
      );
    }

    const stats = (totals) => [
      [t("affiliate.clicks"), formatCount(totals.clicks)],
      [t("affiliate.conversions"), formatCount(totals.conversions)],
      [t("affiliate.pendingEarnings"), `${CURRENCY}${compact(totals.pendingEarnings)}`],
      [t("affiliate.earnings"), `${CURRENCY}${compact(totals.earnings)}`],
    ];

    return (
      <>
        {myTotals && (
          <div className="grid grid-cols-2 gap-space-16 min-[1100px]:grid-cols-[repeat(3,269px)] min-[1100px]:gap-space-24">
            {stats(myTotals).map(([label, value]) => (
              <StatCell key={label} label={label} value={value} />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-space-24">
          {myLinks.map((row) => (
            <section key={row.affiliateId} className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16">
              <div className="flex min-w-0 items-center gap-space-12">
                <Avatar size="m" src={row.logo} name={row.company} />
                <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                  <p className="truncate text-subheadline-semibold text-label-primary">
                    <ProductLink row={row}>{row.product || row.company}</ProductLink>
                  </p>
                  <p className="truncate text-footnote text-label-secondary">
                    <CompanyLink row={row} className="text-label-secondary">
                      {row.company}
                    </CompanyLink>{" "}
                    · {row.commissionType === "fixed" ? `${CURRENCY}${row.commissionAmount}` : `${row.commissionRate}%`}{" "}
                    {t("affiliate.ofPrice")} {CURRENCY}
                    {row.productPrice}
                  </p>
                </div>
                {row.status !== "active" && (
                  <Badge tone={row.status === "pending" ? "warning" : "danger"}>{row.status}</Badge>
                )}
              </div>

              {row.status === "active" && (
                <div className="flex items-center gap-space-8">
                  <div className="flex h-[44px] min-w-0 flex-1 items-center gap-space-8 rounded-[22px] bg-fill-tertiary px-space-16 text-label-secondary">
                    <Icon name="link" size={20} />
                    <span className="min-w-0 flex-1 truncate text-callout text-label-tertiary">{row.link}</span>
                  </div>
                  <Button
                    size="m"
                    variant="tinted"
                    icon={<Icon name={copiedCode === row.code ? "check" : "copy"} size={20} />}
                    onClick={() => copyLink(row.link, row.code)}
                  >
                    {copiedCode === row.code ? t("common.copied") : t("common.copy")}
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-space-16 min-[1100px]:grid-cols-[repeat(3,260px)] min-[1100px]:gap-space-24">
                {stats(row).map(([label, value]) => (
                  <StatCell key={label} label={label} value={value} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </>
    );
  };

  return (
    <PageTransition>
      <Page>
        <Button variant="plain" size="s" className="self-start" icon={<Icon name="chevron-left" size={16} />} onClick={() => navigate(-1)}>
          {t("common.back")}
        </Button>

        <PageHeader title={t("affiliate.marketplaceTitle")} />

        <div className="flex flex-wrap items-center gap-space-24">
          <SegmentedControl
            track="material"
            label={t("affiliate.marketplaceTitle")}
            value={tab}
            onChange={setTab}
            className="min-[600px]:max-w-[320px]"
            options={[
              { value: "marketplace", label: t("affiliate.tabMarketplace") },
              { value: "my-links", label: t("affiliate.tabMyLinks") },
            ]}
          />
          {tab === "marketplace" && searchField("hidden w-[320px] min-[1100px]:flex")}
        </div>

        {tab === "marketplace" && (
          <div className="flex flex-col gap-space-24 min-[700px]:flex-row min-[700px]:items-center min-[700px]:justify-between">
            {industryFilter}
            {searchField("min-[700px]:w-[360px] min-[1100px]:hidden")}
          </div>
        )}

        {tab === "marketplace" ? renderMarketplace() : renderMyLinks()}

        {selected && (
          <AffiliateProgramModal
            program={selected}
            isDark={isDark}
            setToast={setToast}
            onClose={() => setSelected(null)}
            onJoined={handleJoined}
          />
        )}
      </Page>
    </PageTransition>
  );
};

export default AffiliateMarketplacePage;
